import React, { useState, useEffect } from 'react'
import {
  detectAgent,
  listPrinters,
  printZpl,
  getSavedPrinterUid,
  savePrinterUid,
  getSavedDpi,
  saveDpi,
  SUPPORTED_DPIS,
  ERROR_CODES,
  isBraveBrowser
} from '../../utils/labelPrinter'
import { useToast } from '../../hooks/useToast'
import LabelReprintQueue from './LabelReprintQueue'

const API_BASE = import.meta.env.VITE_API_URL || ''
const BROWSER_PRINT_DOWNLOAD_URL =
  'https://www.zebra.com/gb/en/support-downloads/software/printer-software/browser-print.html'

/**
 * Label printing settings + Zebra Browser Print setup notes.
 * Printer choice and dpi persist per browser (localStorage) — warehouse
 * machines may each have a different Zebra attached.
 */
function PrintingSettings() {
  const [agentDetected, setAgentDetected] = useState(null) // null = checking
  const [printers, setPrinters] = useState([])
  const [selectedUid, setSelectedUid] = useState(getSavedPrinterUid() || '')
  const [dpi, setDpi] = useState(getSavedDpi())
  const [testing, setTesting] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    refreshAgent()
  }, [])

  async function refreshAgent() {
    setAgentDetected(null)
    const detected = await detectAgent()
    setAgentDetected(detected)
    if (detected) {
      try {
        setPrinters(await listPrinters())
      } catch (err) {
        console.error('Failed to list printers:', err)
        setPrinters([])
      }
    } else {
      setPrinters([])
    }
  }

  function handlePrinterChange(uid) {
    setSelectedUid(uid)
    savePrinterUid(uid || null)
    toast.success(uid ? 'Default label printer saved' : 'Printer preference cleared')
  }

  function handleDpiChange(value) {
    const parsed = parseInt(value, 10)
    setDpi(parsed)
    saveDpi(parsed)
    toast.success(`Label dpi set to ${parsed}`)
  }

  async function printTestLabel() {
    setTesting(true)
    try {
      const response = await fetch(`${API_BASE}/api/labels/test.zpl?dpi=${dpi}`, { credentials: 'include' })
      const zpl = await response.text()
      if (!response.ok) throw new Error(`Test label fetch failed (HTTP ${response.status})`)
      await printZpl(zpl)
      toast.success('Test label sent — check the printer')
    } catch (err) {
      if (err.code === ERROR_CODES.AGENT_NOT_RUNNING) {
        toast.error('Zebra Browser Print is not running — see the setup notes below')
      } else {
        toast.error(err.message)
      }
    } finally {
      setTesting(false)
    }
  }

  return (
    <div>
      {/* Requests raised from the handheld, which has no printer of its own */}
      <LabelReprintQueue />

      {/* Printer settings */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 style={{ marginBottom: 'var(--space-3)' }}>Label Printing</h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
          <span className="text-sm text-muted">Browser Print agent:</span>
          {agentDetected === null && <span className="text-sm">checking…</span>}
          {agentDetected === true && <span className="badge badge-success">Detected</span>}
          {agentDetected === false && <span className="badge badge-error">Not detected</span>}
          <button className="btn btn-sm btn-secondary" onClick={refreshAgent}>Re-check</button>
        </div>

        {agentDetected === false && isBraveBrowser() && (
          <div
            style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-4)'
            }}
          >
            <p className="text-sm" style={{ fontWeight: 600, marginBottom: 'var(--space-1)' }}>
              This is Brave — the agent may be running but blocked.
            </p>
            <p className="text-sm" style={{ marginBottom: 'var(--space-2)' }}>
              Brave blocks pages from reaching localhost by default. Paste this into the
              address bar, add <code>https://prism-commerce.com</code> under "Allowed to
              access localhost resources", then hit Re-check:
            </p>
            <pre
              style={{
                background: 'var(--color-gray-100)',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                userSelect: 'all',
                margin: 0
              }}
            >brave://settings/content/localhostAccess</pre>
          </div>
        )}

        <div className="filter-section">
          <div className="filter-group filter-group-wide">
            <label className="filter-label">Default label printer (this machine)</label>
            <select
              className="form-input"
              value={selectedUid}
              onChange={e => handlePrinterChange(e.target.value)}
              disabled={!agentDetected}
            >
              <option value="">— Use agent default —</option>
              {printers.map(p => (
                <option key={p.uid} value={p.uid}>
                  {p.name || p.uid} {p.connection ? `(${p.connection})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Printer dpi</label>
            <select className="form-input" value={dpi} onChange={e => handleDpiChange(e.target.value)}>
              {SUPPORTED_DPIS.map(d => (
                <option key={d} value={d}>{d} dpi{d === 203 ? ' (ZD421 standard)' : ''}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginTop: 'var(--space-4)' }}>
          <button className="btn btn-primary" onClick={printTestLabel} disabled={testing}>
            {testing ? 'Printing…' : 'Print test label'}
          </button>
          <span className="text-sm text-muted" style={{ marginLeft: 'var(--space-3)' }}>
            Prints a fixed 4x6 label so the printer can be verified without touching real stock.
          </span>
        </div>
      </div>

      {/* Setup notes */}
      <div className="card">
        <h3 style={{ marginBottom: 'var(--space-3)' }}>Setting up Zebra Browser Print</h3>
        <ol className="text-sm" style={{ paddingLeft: 'var(--space-5)', lineHeight: 2 }}>
          <li>
            Download and install{' '}
            <a href={BROWSER_PRINT_DOWNLOAD_URL} target="_blank" rel="noreferrer">
              Zebra Browser Print
            </a>{' '}
            on the Mac connected to the ZD421 (one install per machine that prints).
          </li>
          <li>Launch it — it runs in the menu bar and listens on localhost.</li>
          <li>
            <strong>Brave only:</strong> allow this site to reach localhost first — paste{' '}
            <code>brave://settings/content/localhostAccess</code> into the address bar and add{' '}
            <code>https://prism-commerce.com</code>. Chrome and Edge need no change.
          </li>
          <li>
            The first time this site prints, Browser Print asks whether to allow{' '}
            <code>prism-commerce.com</code>. Accept — it then appears under Accepted Hosts in
            the agent's settings, and printing silently fails until it does.
          </li>
          <li>Pick your printer above and print a test label.</li>
        </ol>

        <h4 style={{ margin: 'var(--space-4) 0 var(--space-2)' }}>No agent? Print from Terminal</h4>
        <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-2)' }}>
          Every print button falls back to downloading the raw <code>.zpl</code> file. Send it
          straight to the printer with:
        </p>
        <pre
          style={{
            background: 'var(--color-gray-100)',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 12,
            overflowX: 'auto',
            userSelect: 'all'
          }}
        >lp -d Zebra_ZD421_Printer -o raw ~/Downloads/label-&lt;id&gt;.zpl</pre>
        <p className="text-sm text-muted" style={{ marginTop: 'var(--space-2)' }}>
          The printer must be added in macOS Printers &amp; Scanners (its queue name is{' '}
          <code>Zebra_ZD421_Printer</code>). The <code>-o raw</code> flag bypasses the CUPS
          driver — required, or labels come out blank.
        </p>
      </div>

    </div>
  )
}

export default PrintingSettings
